import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import compose from 'recompose/compose';
import withProps from 'recompose/withProps';
import { withSnackbar } from 'notistack';
import getHostName from 'helpers/getHostName';

// Components
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import InputAdornment from '@material-ui/core/InputAdornment';
import TextField from '@material-ui/core/TextField';
import FileCopy from '@material-ui/icons/FileCopy';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import CircularProgress from '@material-ui/core/CircularProgress';
import CopyToClipboard from 'react-copy-to-clipboard';

import Loader from '@sift/skipper/components/Loader';
import ErrorView from '@sift/skipper/components/views/Error';

// Style
import withStyles from '@material-ui/core/styles/withStyles';
import withThemeOverrides from '@sift/skipper/styles/withThemeOverrides';
import deriveLightDark from '@sift/skipper/styles/deriveLightDark';

const RedButton = compose(
  withThemeOverrides(theme => ({
    palette: { primary: deriveLightDark(theme.palette.error.main) },
  })),
  withProps({
    color: 'primary',
  }),
)(Button);

const styles = theme => {
  return {
    textField: {
      width: '100%',
    },
    directLink: {
      textAlign: 'center',
      marginTop: theme.spacing(3),
    },
  };
};

function PreviewDialog({
  classes,
  themeId,
  status,
  open,
  onClose,
  onDestroyAllThemes,
  enqueueSnackbar,
}) {
  const [titleClicks, setTitleClicks] = useState(0);
  const [easterEggDialogOpen, setEasterEggDialogOpen] = useState(false);
  const hostName = getHostName();

  useEffect(() => {
    if (titleClicks < 5) return;
    setEasterEggDialogOpen(true);
  }, [titleClicks]);

  const handleTitleClick = () => setTitleClicks(titleClicks + 1);
  const handleEasterEggClose = () => setEasterEggDialogOpen(false);
  const handleRedClick = async () => {
    await onDestroyAllThemes();
    setEasterEggDialogOpen(false);
  };

  const subdomain = window.location.hostname.split('.')[0];
  const previewUrl = `https://${subdomain}.${hostName}.com/preview-theme/${themeId}`;

  const handleCopy = () => {
    enqueueSnackbar('Copied link to clipboard!');
  };

  return (
    <Fragment>
      <Dialog open={open} onClose={onClose} aria-labelledby="Preview Dialog">
        <Loader
          status={status}
          isLoadingView={<CircularProgress />}
          hasErrorView={<ErrorView />}
          isEmptyView={null}
        >
          <DialogTitle onClick={handleTitleClick}>Theme ready to view!</DialogTitle>
          <DialogContent>
            <DialogContentText>Click below to copy your theme preview hyperlink:</DialogContentText>
            <CopyToClipboard text={previewUrl} onCopy={handleCopy}>
              <TextField
                className={classes.textField}
                id="input-with-icon-textfield"
                value={previewUrl}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FileCopy />
                    </InputAdornment>
                  ),
                }}
              />
            </CopyToClipboard>
            <DialogContentText className={classes.directLink}>
              {/* internal links are fine */}
              {/* eslint-disable-next-line */}
              <a target="_blank" href={previewUrl}>
                Or click here to open it directly
              </a>
            </DialogContentText>
          </DialogContent>
        </Loader>
      </Dialog>
      <Dialog open={easterEggDialogOpen} onClose={handleEasterEggClose}>
        <DialogTitle>Are you sure you want to delete all themes?</DialogTitle>
        <DialogActions>
          <Button onClick={handleEasterEggClose}>Cancel</Button>
          <RedButton onClick={handleRedClick}>Yes</RedButton>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
}

PreviewDialog.propTypes = {
  classes: PropTypes.object.isRequired,
  themeId: PropTypes.string,
  status: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onDestroyAllThemes: PropTypes.func.isRequired,
  enqueueSnackbar: PropTypes.func.isRequired,
};

export default compose(withStyles(styles), withSnackbar)(PreviewDialog);
